import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

// Internal tracked redirect target. Fully data-driven: the destination is
// resolved from the backend on load, so new /go/:slug routes never require
// code changes here — they only require adding a link entry with a
// `redirect: "/go/…"` value to a community article.
//
// Tracking of the click itself happens upstream in FounderLinks (client-side
// PostHog + server beacon). This component only performs the navigation. If
// the visitor lands here directly (bookmark, share), the backend also
// records a server-side click when the /api/go/:slug fallback is hit — see
// server.py.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OutboundRedirect() {
  const { slug: paramSlug } = useParams();
  const slug = paramSlug;
  const [state, setState] = useState({ status: "loading", url: null, label: null });

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    // Noindex this interstitial in case a search engine ever follows a
    // /go/… URL.
    try {
      let m = document.querySelector('meta[name="robots"]');
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", "robots");
        document.head.appendChild(m);
      }
      m.setAttribute("content", "noindex,nofollow");
    } catch { /* no-op */ }

    axios.get(`${API}/go-lookup/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (cancelled) return;
        const { url, brand, destination } = r.data || {};
        if (!url) {
          setState({ status: "not_found", url: null, label: null });
          return;
        }
        const label = brand
          ? `${brand}${destination ? " on " + destination.charAt(0).toUpperCase() + destination.slice(1) : ""}`
          : "the requested destination";
        setState({ status: "redirecting", url, label });
        // Small delay so any tracking beacon fired upstream has time to
        // queue before the tab handoff.
        window.setTimeout(() => { window.location.replace(url); }, 80);
      })
      .catch(() => { if (!cancelled) setState({ status: "not_found", url: null, label: null }); });

    return () => { cancelled = true; };
  }, [slug]);

  return (
    <div className="min-h-[50vh] flex items-center justify-center px-6" data-testid="outbound-redirect">
      <div className="text-center max-w-sm">
        {state.status === "loading" && (
          <p className="font-serif italic text-secondary-custom">Preparing…</p>
        )}
        {state.status === "redirecting" && (
          <>
            <p className="text-[10px] font-sans tracking-[0.35em] uppercase text-[var(--accent-bronze)] font-semibold">
              Continuing to
            </p>
            <p className="mt-4 font-serif italic text-lg text-primary-custom">
              {state.label}
            </p>
            <p className="mt-6 text-xs font-sans text-secondary-custom">
              If your browser does not redirect,&nbsp;
              <a href={state.url} rel="noopener noreferrer" className="underline underline-offset-4">continue here</a>.
            </p>
          </>
        )}
        {state.status === "not_found" && (
          <>
            <h1 className="font-serif text-2xl text-primary-custom">Link not found</h1>
            <p className="mt-4 font-serif italic text-secondary-custom">This referral link is no longer available.</p>
            <Link to="/" className="mt-6 inline-block text-[10px] font-sans tracking-[0.3em] uppercase text-[var(--accent-bronze)]">Return home</Link>
          </>
        )}
      </div>
    </div>
  );
}
